import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  fetchUserProfile,
  fetchAccountTypeOptions,
  updateUserAccountType,
  fetchUserCommandes,
  fetchUserAnnonces,
  fetchUserNotifications,
  fetchUserApps,
  fetchUserMarketRelations,
  type UserProfile,
  type AccountTypeOption,
  type UserCommandeRow,
  type UserAnnonceRow,
  type UserNotificationRow,
  type UserAppRow,
  type UserMarketRelation,
} from '@/lib/userDetails';
import { fetchUserAudit, auditCategoryMeta, type UserAuditEntry } from '@/lib/userAudit';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Fiche détaillée d'un utilisateur.
// Affiche toute son activité sur Zifek (profil, type de compte,
// commandes, annonces, marketplace, applications, notifications)
// et permet au super admin de changer son type de compte.
// ─────────────────────────────────────────────────────────────

interface Props {
  userId: number;
  onClose: () => void;
  onAccountTypeSaved?: (typeId: number, label: string) => void;
}

type TabKey = 'apercu' | 'audit' | 'commandes' | 'annonces' | 'marketplace' | 'apps' | 'notifications';

const PACKAGE_LABELS = ['Gratuit', 'Partenaire', 'Business', 'Premium'];

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusTone(statut: string): string {
  const s = statut.toLowerCase();
  if (s.includes('livr') || s.includes('confirm') || s.includes('pay')) return 'bg-accent-100 text-accent-900';
  if (s.includes('annul') || s.includes('refus')) return 'bg-red-100 text-red-700';
  return 'bg-secondary-100 text-secondary-900';
}

export default function UserDetailModal({ userId, onClose, onAccountTypeSaved }: Props) {
  const [tab, setTab] = useState<TabKey>('apercu');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [typeOptions, setTypeOptions] = useState<AccountTypeOption[]>([]);
  const [commandes, setCommandes] = useState<UserCommandeRow[]>([]);
  const [annonces, setAnnonces] = useState<UserAnnonceRow[]>([]);
  const [relations, setRelations] = useState<UserMarketRelation[]>([]);
  const [apps, setApps] = useState<UserAppRow[]>([]);
  const [notifications, setNotifications] = useState<UserNotificationRow[]>([]);
  const [audit, setAudit] = useState<UserAuditEntry[]>([]);
  const [savingType, setSavingType] = useState(false);
  const [typeSaved, setTypeSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prof, opts, cmds, anns, rels, aps, notifs, adt] = await Promise.all([
        fetchUserProfile(userId),
        fetchAccountTypeOptions(),
        fetchUserCommandes(userId),
        fetchUserAnnonces(userId),
        fetchUserMarketRelations(userId),
        fetchUserApps(userId),
        fetchUserNotifications(userId),
        fetchUserAudit(userId),
      ]);
      if (!prof) throw new Error("Impossible de charger cet utilisateur.");
      setProfile(prof);
      setTypeOptions(opts);
      setCommandes(cmds);
      setAnnonces(anns);
      setRelations(rels);
      setApps(aps);
      setNotifications(notifs);
      setAudit(adt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const money = useCallback((value: string) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toLocaleString('fr-FR')} ${profile?.monaie || ''}`.trim();
  }, [profile?.monaie]);

  const changeType = async (typeId: number) => {
    if (!profile) return;
    const opt = typeOptions.find((t) => t.id === typeId);
    if (!opt) return;
    setSavingType(true);
    setTypeSaved(false);
    try {
      await updateUserAccountType(profile.id, opt.id, opt.nom, profile.type);
      setProfile((p) => (p ? { ...p, typecompte: opt.id, type: opt.nom } : p));
      setTypeSaved(true);
      onAccountTypeSaved?.(opt.id, opt.nom);
      // Rafraîchit le journal d'activité pour refléter le changement.
      fetchUserAudit(profile.id).then(setAudit).catch(() => {});
      window.setTimeout(() => setTypeSaved(false), 2500);
    } catch {
      setError("Échec de la modification du type de compte.");
    } finally {
      setSavingType(false);
    }
  };

  const merchants = useMemo(() => typeOptions.filter((t) => t.statustypecompte !== 1), [typeOptions]);
  const clients = useMemo(() => typeOptions.filter((t) => t.statustypecompte === 1), [typeOptions]);
  const isMerchant = useMemo(() => {
    const opt = typeOptions.find((t) => t.id === profile?.typecompte);
    return opt ? opt.statustypecompte !== 1 : false;
  }, [typeOptions, profile?.typecompte]);
  const shopUrl = profile
    ? `${window.location.origin}${String(__BASE_PATH__ || '/').replace(/\/$/, '')}/boutique/${profile.id}`
    : '';
  const openShop = () => {
    if (shopUrl) window.open(shopUrl, '_blank', 'noopener,noreferrer');
  };
  const unread = notifications.filter((n) => n.isRead === 0).length;
  const commandesVendeur = commandes.filter((c) => c.role === 'vendeur').length;
  const commandesClient = commandes.filter((c) => c.role === 'client').length;
  const publishedCount = annonces.filter((a) => a.publique === 1).length;

  const tabs: { key: TabKey; label: string; icon: string; count?: number }[] = [
    { key: 'apercu', label: 'Vue d\u2019ensemble', icon: 'ri-user-3-line' },
    { key: 'audit', label: 'Activité', icon: 'ri-history-line', count: audit.length },
    { key: 'commandes', label: 'Commandes', icon: 'ri-shopping-cart-line', count: commandes.length },
    { key: 'annonces', label: 'Annonces', icon: 'ri-megaphone-line', count: annonces.length },
    { key: 'marketplace', label: 'Marketplace', icon: 'ri-store-2-line', count: relations.length },
    { key: 'apps', label: 'Applications', icon: 'ri-apps-2-line', count: apps.length },
    { key: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line', count: unread },
  ];

  const avatar = resolveMediaUrl(profile?.image);
  const navigate = useNavigate();

  /**
   * Ouvre la page Statistiques déjà filtrée sur ce site.
   * On mémorise le périmètre (mêmes clés que la page stats) avant de naviguer :
   * la page le restaure automatiquement à l'ouverture.
   */
  const openStats = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    try {
      localStorage.setItem(
        'zifek_superstats_filter',
        JSON.stringify({ startDate: iso(start), endDate: iso(end), scopeId: profile?.id ?? userId }),
      );
    } catch {
      /* stockage indisponible */
    }
    onClose();
    navigate('/superadmin/stats');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-4xl bg-background-50 border border-background-200/70 rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* En-tête */}
        <div className="flex items-start gap-4 px-5 py-4 border-b border-background-200/70">
          <div className="w-12 h-12 rounded-full bg-foreground-100 overflow-hidden flex items-center justify-center flex-shrink-0">
            {avatar ? (
              <img src={avatar} alt={profile?.name || ''} className="w-full h-full object-cover" />
            ) : (
              <span className="text-foreground-700 font-bold">{profile?.name?.charAt(0) || '?'}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base md:text-lg font-bold font-heading text-foreground-950 truncate">
              {profile?.name || 'Utilisateur'}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500 mt-0.5">
              <span>@{profile?.userName || '—'}</span>
              {profile?.email && <span className="truncate max-w-[220px]">{profile.email}</span>}
              <span className="inline-flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${profile?.active === 1 ? 'bg-accent-500' : 'bg-foreground-400'}`} />
                {profile?.active === 1 ? 'Actif' : 'Inactif'}
              </span>
            </div>
          </div>
          {isMerchant && (
            <button
              onClick={openStats}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-100 text-primary-900 text-xs font-semibold hover:bg-primary-200/70 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              title="Ouvrir les statistiques de ce site"
            >
              <i className="ri-bar-chart-box-line"></i>
              Statistiques
            </button>
          )}
          {isMerchant && (
            <button
              onClick={openShop}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              title="Ouvrir la boutique publique de ce commerçant"
            >
              <i className="ri-store-2-line"></i>
              Voir la boutique
            </button>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Onglets */}
        <div className="px-5 pt-3 border-b border-background-200/70 overflow-x-auto">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-3">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  tab === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
                {typeof t.count === 'number' && t.count > 0 && (
                  <span className={`ml-0.5 px-1.5 rounded-full text-[10px] ${tab === t.key ? 'bg-foreground-100 text-foreground-700' : 'bg-background-200/70 text-foreground-600'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error && !profile ? (
            <div className="text-center py-16">
              <i className="ri-error-warning-line text-3xl text-red-400 block mb-3"></i>
              <p className="text-sm text-foreground-600">{error}</p>
              <button onClick={load} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer">
                Réessayer
              </button>
            </div>
          ) : profile ? (
            <>
              {error && (
                <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>{error}
                </div>
              )}

              {tab === 'apercu' && (
                <div className="space-y-5">
                  {/* Stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard icon="ri-shopping-cart-line" label="Commandes" value={String(commandes.length)} sub={`${commandesVendeur} vendeur · ${commandesClient} client`} />
                    <StatCard icon="ri-megaphone-line" label="Annonces" value={String(annonces.length)} sub={`${publishedCount} publiée(s)`} accent />
                    <StatCard icon="ri-apps-2-line" label="Applications" value={String(apps.length)} sub={relations.length > 0 ? `${relations.length} marketplace` : 'Aucune marketplace'} />
                    <StatCard icon="ri-wallet-3-line" label="Solde" value={`${profile.solde.toLocaleString('fr-FR')} ${profile.monaie || ''}`.trim()} sub={PACKAGE_LABELS[profile.package] || `Pack #${profile.package}`} />
                  </div>

                  {/* Type de compte — modifiable */}
                  <div className="border border-primary-200/70 bg-primary-100/40 rounded-xl p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-user-line text-background-50"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950">Type de compte</h4>
                        <p className="text-[11px] text-foreground-500">Modifier le type applique immédiatement les capacités de la plateforme pour ce compte.</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <select
                        value={profile.typecompte}
                        onChange={(e) => changeType(Number(e.target.value))}
                        disabled={savingType}
                        className="flex-1 h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-60"
                      >
                        <optgroup label="Commerçants">
                          {merchants.map((t) => (
                            <option key={t.id} value={t.id}>{t.nom} (#{t.id})</option>
                          ))}
                        </optgroup>
                        <optgroup label="Clients">
                          {clients.map((t) => (
                            <option key={t.id} value={t.id}>{t.nom} (#{t.id})</option>
                          ))}
                        </optgroup>
                      </select>
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-50 border border-background-200/70 text-foreground-700 whitespace-nowrap">
                        {savingType ? <i className="ri-loader-4-line animate-spin"></i> : typeSaved ? <i className="ri-check-line text-accent-600"></i> : <i className="ri-price-tag-3-line"></i>}
                        {typeSaved ? 'Enregistré' : (profile.type || `Type #${profile.typecompte}`)}
                      </span>
                    </div>
                  </div>

                  {/* Profil */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoCard title="Coordonnées" icon="ri-contacts-book-line">
                      <InfoRow label="Nom" value={profile.name} />
                      <InfoRow label="Nom de commerce" value={profile.nomcommerce} />
                      <InfoRow label="Email" value={profile.email} />
                      <InfoRow label="Téléphone" value={profile.telephone} />
                      <InfoRow label="Ville / Pays" value={[profile.ville, profile.pays].filter(Boolean).join(', ')} />
                      <InfoRow label="Adresse" value={[profile.adresse, profile.quartier].filter(Boolean).join(' · ')} />
                    </InfoCard>
                    <InfoCard title="Compte" icon="ri-information-line">
                      <InfoRow label="Identifiant" value={`#${profile.id}`} />
                      <InfoRow label="Inscrit le" value={formatDate(profile.datecreation)} />
                      <InfoRow label="Langue" value={profile.langue} />
                      <InfoRow label="Devise" value={profile.monaie} />
                      <InfoRow label="TVA" value={profile.tva} />
                      <InfoRow label="Présence marketplace" value={profile.presencemarketplace} />
                    </InfoCard>
                  </div>

                  {isMerchant && (
                    <button
                      onClick={openShop}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-secondary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line"></i>
                      Voir la boutique publique de {profile.nomcommerce || profile.name}
                    </button>
                  )}

                  {(profile.description || profile.aboutus) && (
                    <InfoCard title="À propos" icon="ri-file-text-line">
                      <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                        {profile.aboutus || profile.description}
                      </p>
                    </InfoCard>
                  )}
                </div>
              )}

              {tab === 'audit' && (() => {
                const derived: { key: string; date: string | null; icon: string; className: string; label: string; description: string }[] = [];
                commandes.forEach((c) => derived.push({
                  key: `cmd-${c.id}-${c.role}`,
                  date: c.dateTime,
                  icon: 'ri-shopping-cart-line',
                  className: 'text-primary-600 bg-primary-50',
                  label: c.role === 'vendeur' ? 'Commande reçue' : 'Commande passée',
                  description: `#${c.id} · ${c.titre || 'Commande'}${c.totalttc ? ` · ${money(c.totalttc)}` : ''}`,
                }));
                annonces.forEach((a) => derived.push({
                  key: `ann-${a.id}`,
                  date: a.created,
                  icon: 'ri-megaphone-line',
                  className: 'text-secondary-600 bg-secondary-50',
                  label: 'Annonce créée',
                  description: a.titre || `Annonce #${a.id}`,
                }));
                const timeline = [
                  ...audit.map((e) => ({
                    key: `audit-${e.id}`,
                    date: e.createdAt,
                    icon: auditCategoryMeta(e.category).icon,
                    className: auditCategoryMeta(e.category).className,
                    label: auditCategoryMeta(e.category).label,
                    description: e.description || e.action,
                  })),
                  ...derived,
                ]
                  .filter((e) => !!e.date)
                  .sort((a, b) => new Date(b.date as string).getTime() - new Date(a.date as string).getTime())
                  .slice(0, 60);
                const loginCount = audit.filter((e) => e.category === 'login').length;
                const typeCount = audit.filter((e) => e.category === 'type_change').length;
                return (
                  <div className="space-y-5">
                    <div className="grid grid-cols-3 gap-3">
                      <StatCard icon="ri-login-box-line" label="Connexions" value={String(loginCount)} sub="tracées" />
                      <StatCard icon="ri-shield-user-line" label="Type de compte" value={String(typeCount)} sub="changements" accent />
                      <StatCard icon="ri-list-check-2" label="Événements" value={String(timeline.length)} sub="récents" />
                    </div>

                    {timeline.length === 0 ? (
                      <EmptyLine icon="ri-history-line" text="Aucune activité enregistrée pour cet utilisateur." />
                    ) : (
                      <div className="relative">
                        <div className="absolute left-4 top-2 bottom-2 w-px bg-background-200/70" />
                        <div className="space-y-1">
                          {timeline.map((e) => (
                            <div key={e.key} className="relative flex items-start gap-3 pl-0 py-2">
                              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${e.className}`}>
                                <i className={`${e.icon} text-sm`}></i>
                              </div>
                              <div className="min-w-0 flex-1 pt-0.5">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  <span className="text-sm font-medium text-foreground-900">{e.label}</span>
                                  <span className="text-[11px] text-foreground-400">{formatDate(e.date)}</span>
                                </div>
                                <p className="text-xs text-foreground-500 break-words mt-0.5">{e.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {tab === 'commandes' && (
                commandes.length === 0 ? (
                  <EmptyLine icon="ri-shopping-cart-line" text="Aucune commande liée à cet utilisateur." />
                ) : (
                  <div className="border border-background-200/70 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] text-sm">
                        <thead>
                          <tr className="bg-background-100/60 text-left">
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Produit</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Rôle</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Client</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Total</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Statut</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commandes.map((c) => (
                            <tr key={`${c.id}-${c.role}`} className="border-t border-background-200/70">
                              <td className="px-4 py-3 text-foreground-900">{c.titre || '—'}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.role === 'vendeur' ? 'bg-primary-100 text-primary-900' : 'bg-secondary-100 text-secondary-900'}`}>
                                  {c.role === 'vendeur' ? 'Vendeur' : 'Client'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-foreground-600">{c.nomclient || '—'}</td>
                              <td className="px-4 py-3 font-semibold text-foreground-900">{c.totalttc ? money(c.totalttc) : '—'}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(c.statut)}`}>{c.statut}</span>
                              </td>
                              <td className="px-4 py-3 text-xs text-foreground-500">{formatDate(c.dateTime)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}

              {tab === 'annonces' && (
                annonces.length === 0 ? (
                  <EmptyLine icon="ri-megaphone-line" text="Aucune annonce publiée par cet utilisateur." />
                ) : (
                  <div className="space-y-2">
                    {annonces.map((a) => (
                      <div key={a.id} className="flex items-center gap-3 border border-background-200/70 rounded-lg p-3">
                        <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-price-tag-3-line text-foreground-500"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground-900 truncate">{a.titre || '—'}</div>
                          <div className="text-xs text-foreground-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            {a.type && <span className="capitalize">{a.type}</span>}
                            {a.ville && <span>· {a.ville}</span>}
                            <span>· {formatDate(a.created)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {a.isBoost && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-900">À la une</span>}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.publique === 1 ? 'bg-accent-500 text-background-50' : 'bg-foreground-100 text-foreground-600'}`}>
                            {a.publique === 1 ? 'Publiée' : 'Brouillon'}
                          </span>
                          {a.prix && <span className="text-sm font-bold text-primary-600">{money(a.prix)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === 'marketplace' && (
                relations.length === 0 ? (
                  <EmptyLine icon="ri-store-2-line" text="Cet utilisateur n'est rattaché à aucune marketplace." />
                ) : (
                  <div className="space-y-2">
                    {relations.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 border border-background-200/70 rounded-lg p-3">
                        <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-store-2-line text-secondary-700"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground-900 truncate">{r.marketplaceName}</div>
                          <div className="text-xs text-foreground-500">Depuis le {formatDate(r.date)}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                          {r.misEnAvant && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-900">En avant</span>}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            r.validation === 'approuve' ? 'bg-accent-100 text-accent-900' : r.validation === 'refuse' ? 'bg-red-100 text-red-700' : 'bg-secondary-100 text-secondary-900'
                          }`}>
                            {r.validation === 'approuve' ? 'Approuvé' : r.validation === 'refuse' ? 'Refusé' : 'En attente'}
                          </span>
                          {r.statut === 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-foreground-100 text-foreground-600">Masqué</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === 'apps' && (
                apps.length === 0 ? (
                  <EmptyLine icon="ri-apps-2-line" text="Aucune application installée pour cet utilisateur." />
                ) : (
                  <div className="space-y-2">
                    {apps.map((a) => (
                      <div key={a.id} className="flex items-center gap-3 border border-background-200/70 rounded-lg p-3">
                        <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-apps-2-line text-foreground-500"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground-900 truncate">{a.nomapp || 'Application'}</div>
                          {a.nompage && <div className="text-xs text-foreground-500 truncate">{a.nompage}</div>}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.status === 1 ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                          {a.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === 'notifications' && (
                notifications.length === 0 ? (
                  <EmptyLine icon="ri-notification-3-line" text="Aucune notification pour cet utilisateur." />
                ) : (
                  <div className="space-y-2">
                    {notifications.map((n) => (
                      <div key={n.id} className={`flex items-start gap-3 border rounded-lg p-3 ${n.isRead === 0 ? 'border-primary-200/70 bg-primary-100/30' : 'border-background-200/70'}`}>
                        <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-notification-3-line text-foreground-500 text-sm"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground-800 leading-snug">{n.message || '—'}</p>
                          <div className="text-[11px] text-foreground-400 mt-0.5 flex items-center gap-2">
                            <span className="capitalize">{n.type || 'système'}</span>
                            <span>· {formatDate(n.createdAt)}</span>
                          </div>
                        </div>
                        {n.isRead === 0 && <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1"></span>}
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent }: {
  icon: string; label: string; value: string; sub?: string; accent?: boolean;
}) {
  return (
    <div className="border border-background-200/70 rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${accent ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-600'}`}>
        <i className={icon}></i>
      </div>
      <div className="text-lg font-bold text-foreground-950 truncate">{value}</div>
      <div className="text-[11px] text-foreground-500 uppercase tracking-wide">{label}</div>
      {sub && <div className="text-[11px] text-foreground-400 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function InfoCard({ title, icon, children }: { title: string; icon: string; children: ReactNode }) {
  return (
    <div className="border border-background-200/70 rounded-xl p-4">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-3">
        <i className={icon}></i>
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-foreground-500 flex-shrink-0">{label}</span>
      <span className="text-foreground-900 font-medium text-right break-words min-w-0">{value || '—'}</span>
    </div>
  );
}

function EmptyLine({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
        <i className={`${icon} text-xl text-foreground-400`}></i>
      </div>
      <p className="text-sm text-foreground-500">{text}</p>
    </div>
  );
}